import './ExitForm.scss'
import { useState, useMemo, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { IExitForm } from 'Models'
import {
  StepFourComp,
  StepOneComp,
  stepOneSchema,
  StepThreeComp,
  stepThreeSchema,
  StepTwoComp,
  stepTwoSchema,
} from '../../Components'
import employeeService from 'Services/employeeService'
import { handleError, handleSuccess } from 'Helpers'
// import logo from '../../../../Assets/Images/logo-dark.jpeg'
// import Swal from 'sweetalert2'

// convert boolean to string
type FormData = Omit<
  IExitForm,
  'discussedWithSupervisor' | 'recommendEmployer' | 'wouldReapply'
> & {
  discussedWithSupervisor: string | undefined
  recommendEmployer: string | undefined
  wouldReapply: string | undefined
}

export const ExitForm = () => {
  const [countStep, setCountStep] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<any[]>([])

  const defaultValues: FormData = {
    supervisorName: '',
    employmentStartDate: '',
    employmentEndDate: '',
    currentPosition: '',
    returnOf: [],
    reasonsForLeaving: [],
    specificEventPromptedResignation: '',
    discussedWithSupervisor: undefined,
    trainingRating: 0,
    supervisorRelationshipRating: 0,
    fellowEmployeesRelationshipRating: 0,
    salaryRating: 0,
    benefitsRating: 0,
    workloadRating: 0,
    laptopPassword: '',
    overallSatisfactionRating: 0,
    benefitsComments: '',
    problemsEncountered: '',
    enjoyedMost: '',
    enjoyedLeast: '',
    recommendEmployer: undefined,
    wouldReapply: undefined,
    recommendEmployerExplanation: '',
    exitInterviewConductedBy: '',
    dateTime: '',
  }

  const currentSchema: any = useMemo(() => {
    switch (countStep) {
      case 1:
        return stepOneSchema
      case 2:
        return stepTwoSchema
      case 3:
        return stepThreeSchema
      default:
        return stepOneSchema
    }
  }, [countStep])

  //  This would have been cool but we'd loose each step validation and validate all at the end
  //  So, we'll be using any type on useForm, off course we'd loose type safety but our input validation will still work for each step
  //  const mergedSchema = stepOneSchema.concat(stepTwoSchema).concat(stepThreeSchema);

  const methods = useForm<any>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(currentSchema),
  })

  const { handleSubmit, trigger } = methods

  // const totalSteps = 3
  const usePreview = true
  const totalSteps = usePreview ? 4 : 3
  const stepsView = `Step ${countStep} of ${totalSteps}`

  const handlePrevStep = () => setCountStep((prev) => Math.max(prev - 1, 1))

  const handleNextStep = async () => {
    const valid = await trigger()
    if (valid) {
      setCountStep((prev) => Math.min(prev + 1, totalSteps))
    }
  }

  const onSubmit = async (data: IExitForm) => {
    if (countStep !== totalSteps) return
    // console.log('data', data)
    // return

    // const { ratings, ...rest } = data;
    // we cast back to boolean - false === true = false
    // const cleanedData: IExitForm = {
    //   ...data,
    //   discussedWithSupervisor: data.discussedWithSupervisor === 'true',
    //   recommendEmployer: data.recommendEmployer === 'true',
    //   wouldReapply: data.wouldReapply === 'true',
    // }

    setLoading(true)
    // console.log('Form Submitted:', data)
    const res = await employeeService.fillExitForm(data).then(
      () => {
        setLoading(false)
        handleSuccess('Exit form submitted successfully')
      },
      (error) => {
        setLoading(false)
        handleError(error)
      },
    )
  }

  const fetchData = async () => {
    try {
      const response = await employeeService.getAllEmployees()
      // console.log('employee res', response.data.data.dataset)
      const dataSet = response.data.data.dataset
      setData(dataSet)
    } catch (error) {
      // console.log('err', error)
      handleError(error || 'Something went wrong')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <div className='container text-center form-step'>
      <div className='text-start mt-3'>
        <h5 className='text-capitalize'>Exit interview questions</h5>
      </div>
      <div className='card p-3 mb-5 border-0'>
        <div className='card-body'>
          <p>{stepsView}</p>

          <FormProvider {...methods}>
            <form
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              onSubmit={handleSubmit(onSubmit)}
            >
              {countStep === 1 && <StepOneComp data={data} />}
              {countStep === 2 && <StepTwoComp />}
              {countStep === 3 && <StepThreeComp />}
              {usePreview && countStep === 4 && <StepFourComp goToStep={setCountStep} />}

              <div className='mt-2 d-flex justify-content-center gap-5'>
                {countStep > 1 && (
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={handlePrevStep}
                    disabled={countStep === 1}
                  >
                    Prev Step
                  </button>
                )}

                {countStep === totalSteps && usePreview && (
                  <button type='submit' className='btn btn-default' disabled={loading}>
                    Submit {loading && <span className='spinner-border spinner-border-sm'></span>}
                  </button>
                )}
                {countStep < 4 && (
                  <button type='button' className='btn btn-default' onClick={handleNextStep}>
                    Next Step
                  </button>
                )}

                {/* {countStep === totalSteps && usePreview ? (
                <button type='submit' className='btn btn-success'>
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              ) : (
                <button type='button' className='btn btn-primary' onClick={handleNextStep}>
                  Next Step
                </button>
              )} */}
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
}
